import { errorCode, retryAfterSeconds } from '@infrastructure/admin-security-api';
import { adminMailErrorDetail } from '@infrastructure/admin-mail-api';
// .NET char.IsWhiteSpace and regex \s: U+0009-U+000D, U+0085 and the Z categories (not U+FEFF, unlike JavaScript's \s).
const WS = '\\t\\n\\v\\f\\r\\u0085\\p{Zs}\\p{Zl}\\p{Zp}';
const edges = new RegExp(`^[${WS}]+|[${WS}]+$`, 'gu');
export const trimText = (value) => value.replace(edges, '');
const space = new RegExp(`[${WS}]`, 'u');
export const normalizeBody = (body) => body.replace(/\r\n?/g, '\n');
export const lineCount = (body) => normalizeBody(body).split('\n').length;
// Control, format (zero-width, bidi), private-use, unassigned, lone surrogate, line and paragraph separator characters.
// LF is allowed in the body only.
const invalidCharacter = /[\p{Cc}\p{Cf}\p{Co}\p{Cn}\p{Cs}\p{Zl}\p{Zp}]/u;
// `<` followed by an ASCII letter, `/`, `!` or `?`, and template braces.
const tagLike = /<[A-Za-z/!?]|\{\{|\}\}/;
const ASTRAL_LETTER = '一';
const ASTRAL_MARK = '̀';
const ASTRAL_NUMBER = '٠';
const viewOf = (original) => {
    let text = '';
    const starts = [];
    const ends = [];
    for (let i = 0; i < original.length;) {
        const code = original.codePointAt(i);
        const lone = code >= 0xd800 && code <= 0xdfff;
        const length = code > 0xffff ? 2 : 1;
        let mapped = lone ? original[i] : String.fromCodePoint(code).normalize('NFKC');
        mapped = mapped.replace(/。/g, '.');
        if (mapped.length === 2 && mapped.codePointAt(0) > 0xffff)
            mapped = /^\p{L}$/u.test(mapped) ? ASTRAL_LETTER : /^\p{M}$/u.test(mapped) ? ASTRAL_MARK : /^\p{N}$/u.test(mapped) ? ASTRAL_NUMBER : mapped;
        for (let k = 0; k < mapped.length; k++) {
            text += mapped[k];
            starts.push(i);
            ends.push(i + length);
        }
        i += length;
    }
    return { text, starts, ends, original };
};
// The original text behind view positions [start, end).
const originalOf = (view, start, end) => end <= start || start >= view.starts.length ? '' : view.original.slice(view.starts[start], view.ends[Math.min(end, view.ends.length) - 1]);
// The span is written as detection reads it: no compatibility form except the dots 。．｡ (which browsers read as ".").
const plainIn = (view, start, end) => {
    if (end <= start || start >= view.starts.length)
        return true;
    const written = originalOf(view, start, end).replace(/[。．｡]/g, '.');
    return written === view.text.slice(start, Math.min(end, view.text.length));
};
// Every `scheme:` occurrence (two or more scheme characters from an ASCII letter, a colon and a non-space) and every `://`
// (its scheme run, from the first ASCII letter; "-https://x" is checked as "https://x").
const schemeToken = new RegExp(`[A-Za-z][A-Za-z0-9+.\\-]+:(?=[^${WS}])`, 'gu');
const schemeCharacter = /[A-Za-z0-9+.-]/;
const schemeStarts = (text) => {
    const starts = new Set();
    for (const match of text.matchAll(schemeToken))
        starts.add(match.index ?? 0);
    for (let i = text.indexOf('://'); i >= 0; i = text.indexOf('://', i + 1)) {
        let start = i;
        while (start > 0 && schemeCharacter.test(text[start - 1]))
            start--;
        while (start < i && !/[A-Za-z]/.test(text[start]))
            start++;
        starts.add(start);
    }
    return [...starts].sort((a, b) => a - b);
};
// Addresses, including dot-less and IP-literal domains (jan@localhost, jan@[10.0.0.1], jan@10.0.0.1), with marks.
const address = new RegExp(`(?<![\\p{L}\\p{M}\\p{N}._%+\\-])[\\p{L}\\p{M}\\p{N}._%+\\-]+@(?:\\[[^\\]${WS}]*\\]|[\\p{L}\\p{M}\\p{N}\\-]+(?:\\.[\\p{L}\\p{M}\\p{N}\\-]+)*)`, 'gu');
// Only a letter or digit (optionally followed by combining marks) directly before a host or IPv4 makes it part of a longer
// word; a ".", "-", "_", "/", "@", a symbol with a variation selector (❤️evil.com) or a lone mark does not.
const notInWord = '(?<![\\p{L}\\p{N}]\\p{M}*)';
// A bare IPv4 literal (203.0.113.7/login): a host that can never be the portal. .NET \d is any decimal digit.
const bareIp = new RegExp(`${notInWord}\\p{Nd}{1,3}(?:\\.\\p{Nd}{1,3}){3}(?![\\p{L}\\p{M}\\p{N}])(?:[:/?#][^${WS}]*)?`, 'gu');
// www.-prefixed and bare host names (label.tld, optional path) that mail clients turn into links. A trailing "-" ends a host
// ("evil.com-/login" links evil.com); a punycode top-level domain (xn--) is tried first.
const bareHost = new RegExp(`${notInWord}((?:[\\p{L}\\p{N}](?:[\\p{L}\\p{M}\\p{N}\\-]*[\\p{L}\\p{M}\\p{N}])?\\.)+(?:xn--[a-z0-9\\-]{2,59}|\\p{L}[\\p{L}\\p{M}]{1,62}))(?![\\p{L}\\p{M}\\p{N}])((?:[/?#][^${WS}]*)?)`, 'giu');
// Sentence punctuation after a link is not part of it; a closing bracket is, when the token also opened it ("alert(1)").
const trailing = '.,;:!?)]}\'"»”’>';
const count = (text, c) => text.split(c).length - 1;
const trimmedEnd = (text, start, end) => {
    while (end > start && trailing.includes(text[end - 1])) {
        const last = text[end - 1];
        const open = last === ')' ? '(' : last === ']' ? '[' : last === '}' ? '{' : '';
        if (open && count(text.slice(start, end), open) >= count(text.slice(start, end), last))
            break;
        end--;
    }
    return end;
};
export const trimToken = (token) => token.slice(0, trimmedEnd(token, 0, token.length));
const tokenEnd = (text, start) => {
    let end = start;
    while (end < text.length && !space.test(text[end]))
        end++;
    return trimmedEnd(text, start, end);
};
const authorityEnd = (text, start, end) => {
    if (start >= end)
        return end;
    const rest = text.slice(start, end).search(/[/?#]/);
    return rest < 0 ? end : start + rest;
};
const asciiHost = (host) => {
    try {
        return new URL(`https://${host}`).hostname.toLowerCase() || null;
    }
    catch {
        return null;
    }
};
const ipHost = (host) => /^\d{1,3}(\.\d{1,3}){3}$/.test(host) || host.startsWith('[');
// https only; no backslash anywhere (browsers read it as "/", .NET as user info); the authority has no user info, port (also
// :443), percent-encoding or IP literal; the host is a dotted DNS name on the allowlist, in its ASCII (IDN) form, so a
// homograph never equals the portal host.
const httpsLinkAllowed = (token, policy) => {
    if (!/^https:\/\//i.test(token) || token.includes('\\'))
        return false;
    const authority = token.slice('https://'.length).split(/[/?#]/)[0];
    if (!authority || /[@:\\%[\]]/.test(authority))
        return false;
    let url;
    try {
        url = new URL(token);
    }
    catch {
        return false;
    }
    const host = url.hostname.toLowerCase();
    return url.protocol === 'https:' && !url.username && !url.password && !url.port && !ipHost(host) && host.includes('.') && policy.hosts.includes(host);
};
const bareHostAllowed = (host, policy) => {
    const ascii = asciiHost(host.toLowerCase());
    return !!ascii && policy.hosts.includes(ascii);
};
// Same length, so later patterns cannot match across a token that was already judged.
const blank = (value, from, length) => value.slice(0, from) + ' '.repeat(Math.max(0, length)) + value.slice(from + Math.max(0, length));
const characterIssues = (field, value) => invalidCharacter.test(field === 'body' ? value.replace(/\n/g, '') : value) ? [{ field, code: 'INVALID_CHARACTERS' }] : [];
const plainTextIssues = (field, value) => (tagLike.test(value) ? [{ field, code: 'PLAIN_TEXT_ONLY' }] : []);
const linkIssues = (field, value, policy) => {
    const issues = [];
    const view = viewOf(value);
    const text = view.text;
    let rest = text;
    // Every start is judged, as the server does; one inside a link that was already refused is not reported twice.
    let refusedUntil = -1;
    for (const start of schemeStarts(text)) {
        const end = tokenEnd(text, start);
        if (start >= refusedUntil && (end <= start || !httpsLinkAllowed(text.slice(start, end), policy) || !plainIn(view, start + 8, authorityEnd(text, start + 8, end)))) {
            issues.push({ field, code: 'LINK_NOT_ALLOWED', token: originalOf(view, start, Math.max(end, start + 1)) });
            refusedUntil = end;
        }
        rest = blank(rest, start, end - start);
    }
    const withoutLinks = rest;
    for (const match of withoutLinks.matchAll(address)) {
        const index = match.index ?? 0;
        const end = trimmedEnd(withoutLinks, index, index + match[0].length);
        const token = withoutLinks.slice(index, end);
        if (!policy.replyTo || token.toLowerCase() !== policy.replyTo.toLowerCase() || !plainIn(view, index, end))
            issues.push({ field, code: 'ADDRESS_NOT_ALLOWED', token: originalOf(view, index, end) });
        rest = blank(rest, index, match[0].length);
    }
    const remaining = rest;
    for (const match of remaining.matchAll(bareIp)) {
        const index = match.index ?? 0;
        issues.push({ field, code: 'LINK_NOT_ALLOWED', token: originalOf(view, index, trimmedEnd(remaining, index, index + match[0].length)) });
    }
    for (const match of remaining.matchAll(bareHost)) {
        const index = match.index ?? 0;
        const host = match[1];
        if (!bareHostAllowed(host, policy) || !plainIn(view, index, index + host.length))
            issues.push({ field, code: 'LINK_NOT_ALLOWED', token: originalOf(view, index, trimmedEnd(remaining, index, index + match[0].length)) });
    }
    return issues;
};
const subjectFormIssues = (subject, policy) => {
    const trimmed = trimText(subject);
    const issues = [];
    if (/[\r\n\u2028\u2029\u0085]/.test(subject))
        issues.push({ field: 'subject', code: 'SUBJECT_SINGLE_LINE' });
    if (trimmed.length < policy.subjectMin || trimmed.length > policy.subjectMax)
        issues.push({ field: 'subject', code: 'SUBJECT_LENGTH', count: trimmed.length });
    return issues;
};
const bodyFormIssues = (normalized, policy) => {
    const issues = [];
    if (trimText(normalized).length < Math.max(1, policy.bodyMin) || normalized.length > policy.bodyMax)
        issues.push({ field: 'body', code: 'BODY_LENGTH', count: normalized.length });
    const lines = normalized.split('\n').length;
    if (lines > policy.bodyMaxLines)
        issues.push({ field: 'body', code: 'BODY_LINES', count: lines });
    return issues;
};
export const validateSubject = (subject, policy) => {
    const trimmed = trimText(subject);
    return [...subjectFormIssues(subject, policy), ...characterIssues('subject', trimmed), ...plainTextIssues('subject', trimmed), ...linkIssues('subject', trimmed, policy)];
};
export const validateBody = (body, policy) => {
    const normalized = normalizeBody(body);
    return [...bodyFormIssues(normalized, policy), ...characterIssues('body', normalized), ...plainTextIssues('body', normalized), ...linkIssues('body', normalized, policy)];
};
// Every issue of both fields, in the server's order (form of subject and body, then characters, plain text and links, each
// subject before body), so the first one is the server's refusal.
export const validateCustomMail = (input, policy) => {
    const subject = trimText(input.subject);
    const body = normalizeBody(input.body);
    return [
        ...subjectFormIssues(input.subject, policy), ...bodyFormIssues(body, policy),
        ...characterIssues('subject', subject), ...characterIssues('body', body),
        ...plainTextIssues('subject', subject), ...plainTextIssues('body', body),
        ...linkIssues('subject', subject, policy), ...linkIssues('body', body, policy)
    ];
};
// ---- Dutch texts -----------------------------------------------------------------------------------------------------
const hostList = (policy) => (policy?.hosts.length ? policy.hosts.join(', ') : '');
export const issueText = (issue, policy) => {
    const token = issue.token ? `“${issue.token}”` : '';
    switch (issue.code) {
        case 'SUBJECT_LENGTH':
            return `Het onderwerp moet ${policy?.subjectMin ?? 3} tot ${policy?.subjectMax ?? 150} tekens zijn${issue.count != null ? ` (nu ${issue.count})` : ''}.`;
        case 'SUBJECT_SINGLE_LINE':
            return 'Het onderwerp moet op één regel staan.';
        case 'BODY_LENGTH':
            return `Het bericht moet ${Math.max(1, policy?.bodyMin ?? 1)} tot ${policy?.bodyMax ?? 4000} tekens zijn${issue.count != null ? ` (nu ${issue.count})` : ''}.`;
        case 'BODY_LINES':
            return `Het bericht mag hoogstens ${policy?.bodyMaxLines ?? 50} regels hebben${issue.count != null ? ` (nu ${issue.count})` : ''}.`;
        case 'INVALID_CHARACTERS':
            return 'Verwijder onzichtbare tekens en stuurtekens, zoals tabs, regelscheidingstekens, zero-width- of richtingstekens.';
        case 'PLAIN_TEXT_ONLY':
            return 'Alleen platte tekst: geen HTML-tags (zoals <b>) en geen {{ of }}.';
        case 'LINK_NOT_ALLOWED':
            return `${token ? `${token} telt als link en is` : 'Deze link is'} niet toegestaan. ${hostList(policy) ? `Alleen https-links naar ${hostList(policy)} mogen.` : 'Alleen https-links naar de Rebels-portal mogen.'} Geen link bedoeld? Formuleer het anders.`;
        case 'ADDRESS_NOT_ALLOWED':
            return `E-mailadressen zijn niet toegestaan${token ? ` (${token})` : ''}${policy?.replyTo ? `, behalve ${policy.replyTo}` : ''}.`;
        default:
            return `Niet toegestaan: ${issue.code}.`;
    }
};
export const validatorCodes = new Set(['SUBJECT_LENGTH', 'SUBJECT_SINGLE_LINE', 'BODY_LENGTH', 'BODY_LINES', 'INVALID_CHARACTERS', 'PLAIN_TEXT_ONLY', 'LINK_NOT_ALLOWED', 'ADDRESS_NOT_ALLOWED']);
// Review L9: a content refusal of the API that names its field (api-contract §6), shown under that field.
export const serverContentIssue = (error) => {
    const code = errorCode(error);
    const detail = adminMailErrorDetail(error);
    return validatorCodes.has(code) && detail.field ? { field: detail.field, code, token: detail.token ?? undefined } : null;
};
const wait = (error) => {
    const seconds = retryAfterSeconds(error);
    if (!seconds)
        return 'later';
    if (seconds < 90)
        return `over ${Math.max(1, Math.ceil(seconds))} seconden`;
    const minutes = Math.ceil(seconds / 60);
    if (minutes < 90)
        return `over ${minutes} ${minutes === 1 ? 'minuut' : 'minuten'}`;
    return `over ${Math.ceil(minutes / 60)} uur`;
};
const notResendable = {
    TEST: 'Een testmail kan niet opnieuw worden verstuurd.',
    DEFINITION: 'Dit soort mail kan niet opnieuw worden verstuurd. Gebruik de eigen flow, bijvoorbeeld een nieuwe uitnodiging of wachtwoordreset.',
    IN_FLIGHT: 'Deze mail wordt nog verwerkt. Wacht tot de verzending is afgerond.',
    NO_REPLAY: 'De oorspronkelijke tekst (zoals de reden van afwijzing) is niet meer beschikbaar, dus deze mail kan niet opnieuw worden verstuurd.',
    DISABLED: 'Opnieuw versturen is uitgeschakeld voor de Rebels-portal.',
    RESEND_LIMIT: 'Deze mail is al het maximum aantal keren opnieuw verstuurd.'
};
const resendLimit = {
    MAXIMUM: 'Deze mail is al het maximum aantal keren opnieuw verstuurd.',
    IN_FLIGHT: 'De vorige herverzending van deze mail wordt nog verwerkt. Wacht tot die is afgerond.'
};
// Review L9: every code of api-contract §6 (plus the shared session codes) has its own Dutch text.
export const fixedMessages = {
    ADMIN_MAIL_DISABLED: 'Deze functie is uitgeschakeld voor de Rebels-portal. Er is niets verstuurd.',
    MESSAGE_NOT_FOUND: 'Deze mail bestaat niet meer. Vernieuw de Verzendhistorie.',
    RECIPIENT_NOT_ALLOWED: 'In deze omgeving mogen alleen testontvangers mail krijgen. Er is niets verstuurd.',
    REQUEST_KEY_REUSED: 'Deze aanvraag is al voor een andere mail of andere inhoud gebruikt. Bekijk het voorbeeld opnieuw en verstuur dan.',
    REQUEST_INVALID: 'De aanvraag is onvolledig. Vernieuw de pagina en probeer het opnieuw.',
    MAIL_OUTCOME_UNKNOWN: 'Het is niet zeker of de mail is verstuurd. Kies opnieuw Versturen: dezelfde aanvraag wordt niet dubbel verstuurd.',
    MAIL_BUSY: 'Er worden nu veel mails tegelijk verwerkt. Kies opnieuw Versturen: dezelfde aanvraag wordt niet dubbel verstuurd.',
    RECIPIENT_NOT_FOUND: 'De ontvanger is niet gevonden als actief, bevestigd account, of het adres hoort nu bij een ander account. Stuur zo nodig een eigen bericht.',
    RECIPIENT_SUPPRESSED: 'Het adres van deze ontvanger is geblokkeerd na een bounce of spamklacht. Er is niets verstuurd.',
    RECIPIENT_CHANGED: 'De ontvanger is gewijzigd sinds het voorbeeld. Bekijk het voorbeeld opnieuw.',
    TEMPLATE_CHANGED: 'De template is gewijzigd sinds het voorbeeld. Controleer het nieuwe voorbeeld en verstuur opnieuw.',
    CONTENT_CHANGED: 'De inhoud is gewijzigd sinds het voorbeeld (de tekst, het adres van de ontvanger of de templateversie). Controleer het nieuwe voorbeeld en verstuur opnieuw.',
    MAIL_UNAVAILABLE: 'Deze mail is uitgeschakeld, gepauzeerd of niet gepubliceerd. Er is niets verstuurd.',
    MAIL_NOT_QUEUED: 'De mail kon niet in de wachtrij worden gezet. Er is niets verstuurd.',
    NOT_AVAILABLE: 'Deze actie is niet beschikbaar. Er is niets verstuurd.',
    STRONG_SESSION_REQUIRED: 'Log opnieuw in met je passkey of authenticator-app om deze actie te gebruiken.',
    STEP_UP_REQUIRED: 'De server vraagt een extra bevestiging die de portal voor deze actie niet gebruikt. Er is niets verstuurd.',
    SESSION_INVALID: 'Je sessie is verlopen. Log opnieuw in.',
    FORBIDDEN: 'Je hebt geen rechten voor deze actie.'
};
// Messages for the API's UPPER_SNAKE codes (read with the shared errorCode helper). `sending` marks a send: an
// unanswered send may still have been queued, so the admin is told to check the history instead of retrying blindly.
export const adminMailMessage = (error, policy, sending = false) => {
    const code = errorCode(error);
    const detail = adminMailErrorDetail(error);
    if (code === 'RATE_LIMITED')
        return `Je hebt het maximum aantal mails voor nu bereikt. Probeer het ${wait(error)} opnieuw.`;
    if (code === 'RECIPIENT_LIMIT')
        return `Deze ontvanger heeft vandaag al het maximum aantal berichten gekregen. Probeer het ${wait(error)} opnieuw.`;
    if (code === 'PORTAL_LIMIT')
        return `Het dagelijkse maximum voor beheerdersmails in de Rebels-portal is bereikt. Probeer het ${wait(error)} opnieuw.`;
    if (code === 'NOT_RESENDABLE')
        return (detail.reason && notResendable[detail.reason]) || 'Deze mail kan niet opnieuw worden verstuurd.';
    if (code === 'RESEND_LIMIT')
        return (detail.reason && resendLimit[detail.reason]) || resendLimit.MAXIMUM;
    // The acting admin's own name (the mail's "Sent by …") is checked by the same rules and never rewritten.
    if (code === 'SENDER_NAME_NOT_ALLOWED')
        return `Je eigen naam staat als afzender in de mail (“Sent by …”), maar bevat ${detail.reason === 'ADDRESS_NOT_ALLOWED' ? 'een e-mailadres' : detail.reason === 'INVALID_CHARACTERS' ? 'onzichtbare tekens' : 'een link'}${detail.token ? ` (“${detail.token}”)` : ''}. Pas je naam aan in je profiel en probeer het opnieuw. Er is niets verstuurd.`;
    if (validatorCodes.has(code))
        return issueText({ code, token: detail.token }, policy);
    if (fixedMessages[code])
        return fixedMessages[code];
    const status = error?.response?.status;
    if (status === 403)
        return fixedMessages.FORBIDDEN;
    if (status === 401)
        return fixedMessages.SESSION_INVALID;
    if (sending && (!status || status >= 500))
        return 'Het is niet zeker of de mail is verstuurd. Kies opnieuw Versturen (dezelfde aanvraag wordt niet dubbel verstuurd) of controleer de Verzendhistorie.';
    return code !== 'REQUEST_FAILED' ? `Bewerking niet voltooid: ${code}` : 'E-mailbeheer is niet beschikbaar. Controleer de verbinding of probeer opnieuw.';
};
export const adminSendLabel = (send) => {
    const who = send.actorName || 'een beheerder';
    const base = send.kind === 'custom' ? `Eigen bericht van ${who}` : `Opnieuw verstuurd door ${who}`;
    return send.source && send.source !== 'Portal' ? `${base} · ${send.source}` : base;
};
